import IconButton from '@material-ui/core/IconButton';
import Portal from '@material-ui/core/Portal';
import Slider from '@material-ui/core/Slider';
import CloseIcon from '@material-ui/icons/Close';
import { MagnifyingGlassMinus, MagnifyingGlassPlus } from '@phosphor-icons/react';
import { gameBookTemplateActions } from '@orientaction/api-actions';
import html2canvas from 'html2canvas';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { redesignTheme } from '../../../../pages/Redesign/theme';
import { ReactComponent as R1 } from '../../../../Asset/svg/R1.svg';
import { ReactComponent as R2 } from '../../../../Asset/svg/R2.svg';
import { ReactComponent as R3 } from '../../../../Asset/svg/R3.svg';
import { ReactComponent as R4 } from '../../../../Asset/svg/R4.svg';
import { ReactComponent as R5 } from '../../../../Asset/svg/R5.svg';
import { apiServiceNative } from '../../../../utils/fetchNative';
import Loader from '../../../../pages/AdminGame/EditPage/loader-edit';
import { useModalEditImgStyles } from './style';

const { colors } = redesignTheme;

interface IProps {
  fileUrl?: string;
  open: boolean;
  handleClose?: () => void;
  width?: any;
  height?: any;
  handleUrl?: (url: any) => void;
  imgHeightB?: any;
  imgWidthB?: any;
  isVariable?: boolean;
  handleImageVariable?: (data: any) => void;
  viewOnly?: boolean;
}

export const MODAL_IMAGE_WIDTH = 508;

export const getModalImageHeightForTemplate = (template?: number) => {
  if (template === 4 || template === 6 || template === 7) {
    return 622;
  }

  return 410;
};

const ModalEditImg: FC<IProps> = ({
  fileUrl,
  open,
  handleClose,
  width = '100%',
  height = 'auto',
  handleUrl,
  imgHeightB,
  imgWidthB,
  isVariable = false,
  handleImageVariable,
  viewOnly = false,
}) => {
  const classes = useModalEditImgStyles();
  const LINK_IMG = 'https://storage.googleapis.com/orient-action.appspot.com/';
  const [scale, setScale] = useState<number>(1);
  const [isLong, setIsLong] = useState<any>('square');
  const [position, setPosition] = useState<any>({ x: 0, y: 0 });
  const [loading, setLoading] = useState<boolean>(false);
  const imageRef = useRef<any>(null);
  const containerRef = useRef<any>(null);
  const [widthInit, setWidthInit] = useState<any>(width);
  const [heightInit, setHeightInit] = useState<any>(height);
  const dispatch = useDispatch();

  const handleChange = (_event: unknown, newValue: number | number[]) => {
    setScale(newValue as number);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.1, 1));
  };

  useEffect(() => {
    const image = imageRef?.current;
    const containerBounds = containerRef?.current?.getBoundingClientRect();
    const imageBounds = imageRef?.current?.getBoundingClientRect();
    let isDragging = false;

    const handleMouseDown = () => {
      isDragging = scale > 1;
    };

    const handleMouseMove = (e: any) => {
      if (!isDragging || !containerBounds || !imageBounds) {
        return;
      }

      if (imageBounds.width > containerBounds.width) {
        setPosition((prevPos: any) => {
          const newX = prevPos.x + e.movementX;
          const maxTranslateX = (imageBounds.width - containerBounds.width) / 2;
          return {
            x: Math.max(Math.min(newX, maxTranslateX), -maxTranslateX),
            y: prevPos.y,
          };
        });
      }

      if (imageBounds.height > containerBounds.height) {
        setPosition((prevPos: any) => {
          const newY = prevPos.y + e.movementY;
          const maxTranslateY = (imageBounds.height - containerBounds.height) / 2;
          return {
            x: prevPos.x,
            y: Math.max(Math.min(newY, maxTranslateY), -maxTranslateY),
          };
        });
      }
    };

    const handleMouseUp = () => {
      isDragging = false;
    };

    image?.addEventListener('mousedown', handleMouseDown);
    image?.addEventListener('mousemove', handleMouseMove);
    image?.addEventListener('mouseup', handleMouseUp);

    return () => {
      image?.removeEventListener('mousedown', handleMouseDown);
      image?.removeEventListener('mousemove', handleMouseMove);
      image?.removeEventListener('mouseup', handleMouseUp);
    };
  }, [scale, fileUrl, widthInit, heightInit]);

  useEffect(() => {
    if (containerRef?.current && imageRef?.current) {
      const containerBounds = containerRef.current.getBoundingClientRect();
      const imageBounds = imageRef.current.getBoundingClientRect();

      if (imageBounds.width > containerBounds.width) {
        setPosition((prevPos: any) => {
          const maxTranslateX = (imageBounds.width - containerBounds.width) / 2;
          return {
            x: Math.max(Math.min(prevPos.x, maxTranslateX), -maxTranslateX),
            y: prevPos.y,
          };
        });
      }

      if (imageBounds.height > containerBounds.height) {
        setPosition((prevPos: any) => {
          const maxTranslateY = (imageBounds.height - containerBounds.height) / 2;
          return {
            x: prevPos.x,
            y: Math.max(Math.min(prevPos.y, maxTranslateY), -maxTranslateY),
          };
        });
      }
    }
  }, [scale, widthInit, heightInit]);

  function base64ToBlob(base64: string): Blob {
    const mime = 'image/png';
    const sliceSize = 1024;
    const base64Data = base64.split(',')[1] || base64;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);
      const bytes = new Array(end - begin);

      for (let offset = begin, i = 0; offset < end; ++offset, ++i) {
        bytes[i] = byteCharacters.charCodeAt(offset);
      }

      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }

    return new Blob(byteArrays, { type: mime });
  }

  const uploadToServer = async (file: any) => {
    const formData = new FormData();
    formData.append('files', file);
    const response = await apiServiceNative.post(`/api/upload`, formData, true);

    if (isVariable) {
      handleImageVariable?.({
        id: response[0]?.id,
        url: response[0]?.url,
      });
    } else {
      dispatch(
        gameBookTemplateActions.updateField('illustration', {
          id: response[0]?.id,
          url: response[0]?.url,
        }),
      );
    }

    let idFILESTemps = localStorage.getItem('idFILESTemps') as any;
    if (!idFILESTemps) {
      idFILESTemps = [];
    } else {
      idFILESTemps = JSON.parse(idFILESTemps);
    }
    idFILESTemps.push(response[0]?.id);
    localStorage.setItem('idFILESTemps', JSON.stringify(idFILESTemps));
  };

  const captureDivToImage = () => {
    if (!containerRef.current) {
      return;
    }

    if (fileUrl?.includes(LINK_IMG) && scale <= 1) {
      handleClose?.();
      return;
    }

    setLoading(true);
    containerRef.current.style.backgroundColor = 'transparent';
    imageRef.current.style.objectFit = 'initial';

    html2canvas(containerRef.current, { useCORS: true }).then(async canvas => {
      const imgData = canvas.toDataURL('image/png');
      containerRef.current.style.backgroundColor = colors.progressTrackMuted;

      if (handleUrl) {
        await uploadToServer(base64ToBlob(imgData));
        handleUrl(imgData);
        setScale(1);
        setPosition({ x: 0, y: 0 });
        handleClose?.();
        setLoading(false);
      }
    });
  };

  const closeModal = () => {
    if (!handleClose) {
      return;
    }

    if (viewOnly || fileUrl?.includes(LINK_IMG)) {
      handleClose();
    } else {
      handleUrl?.('');
      handleClose();
    }

    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (imgHeightB && imgWidthB) {
      if (imgHeightB > imgWidthB) {
        setIsLong('longV');
      } else if (imgHeightB < imgWidthB) {
        setIsLong('longH');
      } else {
        setIsLong('square');
      }
    }
  }, [imgHeightB, imgWidthB]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setWidthInit(width);
    setHeightInit(height);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [open, width, height]);

  const typeShow = [
    { text: '4:3', height: 410, icon: <R1 /> },
    { text: '3:2', height: 380, icon: <R2 /> },
    { text: '1:1', height: 508, icon: <R3 /> },
    { text: '1:2', height: 622, icon: <R4 /> },
    { text: '3:2', height: 642, icon: <R5 /> },
  ];

  if (!open) {
    return null;
  }

  const handleOverlayClose = () => {
    if (viewOnly) {
      closeModal();
    }
  };

  return (
    <Portal>
      <div
        className={classes.overlay}
        role="presentation"
        onClick={event => {
          if (event.target === event.currentTarget) {
            handleOverlayClose();
          }
        }}>
        <div className={classes.modal} role="dialog" aria-modal="true" aria-label="Édition d'image">
          <IconButton className={classes.closeButton} onClick={closeModal} aria-label="Fermer">
            <CloseIcon />
          </IconButton>

          <div className={classes.zoomRow}>
            <IconButton className={classes.zoomButton} onClick={handleZoomOut} aria-label="Zoom arrière">
              <MagnifyingGlassMinus size={18} weight="bold" />
            </IconButton>
            <Slider
              className={classes.slider}
              value={scale}
              min={1}
              step={0.0001}
              max={2}
              onChange={handleChange}
              aria-label="Niveau de zoom"
            />
            <IconButton className={classes.zoomButton} onClick={handleZoomIn} aria-label="Zoom avant">
              <MagnifyingGlassPlus size={18} weight="bold" />
            </IconButton>
          </div>

          <div className={classes.imageContainer}>
            <div className={classes.editorColumn} style={{ width: widthInit }}>
              <div
                ref={containerRef}
                className={classes.imageFrame}
                style={{ width: widthInit, height: heightInit }}>
                <img
                  ref={imageRef}
                  className={classes.image}
                  style={{
                    width:
                      isLong === 'longV'
                        ? 'auto'
                        : isLong === 'square' && widthInit > heightInit
                        ? 'auto'
                        : '100%',
                    height:
                      isLong === 'longH'
                        ? 'auto'
                        : isLong === 'square' && widthInit < heightInit
                        ? 'auto'
                        : '100%',
                    scale: `${scale}`,
                    translate: `${position.x}px ${position.y}px`,
                  }}
                  src={fileUrl}
                  alt=""
                  draggable={false}
                />
              </div>

              {!viewOnly && (
                <>
                  <div className={classes.ratios}>
                    {typeShow.map(type => (
                      <button
                        key={type.text}
                        type="button"
                        className={classes.ratioItem}
                        onClick={() => {
                          setHeightInit(type.height);
                          setScale(1);
                          setPosition({ x: 0, y: 0 });
                        }}>
                        <span className={classes.ratioIcon}>{type.icon}</span>
                        <span className={classes.ratioLabel}>{type.text}</span>
                      </button>
                    ))}
                  </div>
                  <button type="button" className={classes.submitButton} onClick={captureDivToImage}>
                    Valider les changements
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {!viewOnly && <Loader open={loading} text="Upload en cours..." />}
    </Portal>
  );
};

export default ModalEditImg;
